import React from "react";
import * as RadixCheckbox from "@radix-ui/react-checkbox";
import { useId } from "@radix-ui/react-id";
import styled from "styled-components";

interface CheckboxProps {
  readonly name?: string;
  readonly label?: string;
  readonly isChecked: RadixCheckbox.CheckedState;
  readonly onCheckedChange: () => void;
}
/**
 * A checkbox backed by a React state.
 */
export function Checkbox({
  name,
  isChecked,
  onCheckedChange,
  label,
}: CheckboxProps): React.JSX.Element {
  const checkboxId = useId();
  return (
    <CheckboxContainer>
      <StyledCheckboxRoot
        name={name}
        id={checkboxId}
        checked={isChecked}
        $isChecked={isChecked}
        onCheckedChange={onCheckedChange}
      >
        <StyledCheckIndicator>
          <tractie-icon
            name={isChecked === "indeterminate" ? "minus" : "check"}
            size="xs"
            stroke-width="thick"
            color="--tractie-color-content-default-inverse"
          />
        </StyledCheckIndicator>
      </StyledCheckboxRoot>
      {label && <StyledCheckboxLabel htmlFor={checkboxId}>{label}</StyledCheckboxLabel>}
    </CheckboxContainer>
  );
}

const CheckboxContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.spacing2.toString()};
  align-items: center;
`;

const StyledCheckboxRoot = styled(RadixCheckbox.Root)<{ $isChecked: RadixCheckbox.CheckedState }>`
  background-color: ${({ $isChecked, theme }) =>
    !$isChecked ? theme.colors.backgroundInput : theme.colors.contentSuccess};
  border-style: ${(props) => (props.$isChecked === false ? "solid" : "none")};
  width: 16px;
  height: 16px;
  border-width: 1px;
  border-color: ${({ theme }) => theme.colors.borderInput};
  border-radius: 3px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color 0.3s;

  &:hover {
    border-color: ${({ theme }) => theme.colors.borderInputHover};
  }

  &:focus {
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.borderFocus};
    outline: none;
  }
`;

const StyledCheckIndicator = styled(RadixCheckbox.Indicator)`
  display: flex;
  align-items: center;
`;

const StyledCheckboxLabel = styled.label`
  user-select: none;
`;
